/**
 * MilestoneRevealOverlay — celebrates a newly-unlocked artifact.
 *
 * Reads the head of `useArtifactsStore.revealQueue` and renders one of:
 *
 *   - `major` weight: full-screen backdrop with Sai-voice text + a small
 *     preview disc + a `<DelayedButton>` «продолжить» (1s fill).
 *
 *   - `minor` weight: top-right corner toast that:
 *       • НЕ пропадает само (JB 2026-05-25: «должны не пропадать
 *         дольше либо иметь крестик»). Закрытие — только через крестик,
 *         swipe вправо, или клик → expand в полноценный showcase.
 *       • При клике (не на крестик) → раскрывается в major-style overlay
 *         с preview артефакта чтобы игрок увидел что появилось.
 *       • Swipe-to-dismiss: drag вправо на 80px смахивает тост.
 *
 *   - `summary` queue entry: показывает список из N имён артефактов
 *     с тем же `<DelayedButton>` dismiss.
 *
 * При dismiss → `dismissCurrentReveal()` продвигается на следующий
 * элемент очереди.
 *
 * Wire site: один экземпляр в App.tsx, z-index 100.
 */
import { motion, type PanInfo } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { DelayedButton } from '../components/DelayedButton.js';
import { useOnboardingStore } from '../onboarding/store.js';
import { emit } from '../telemetry/emit.js';
import { ArtifactPreview } from './ArtifactPreview.js';
import { describeArtifactTriggerPast } from './describeTrigger.js';
import { useArtifactsStore } from './store.js';
import styles from './MilestoneRevealOverlay.module.css';

/**
 * Swipe-to-dismiss threshold — пользователь должен потянуть тост на 80px
 * вправо чтобы смахнуть. Меньше — отпустит и тост вернётся на место.
 */
const SWIPE_DISMISS_PX = 80;

export function MilestoneRevealOverlay(): JSX.Element | null {
  const queue = useArtifactsStore((s) => s.revealQueue);
  const dismiss = useArtifactsStore((s) => s.dismissCurrentReveal);
  // JB 2026-05-26: пока активна подсказка `artifacts_explained` — НЕ
  // рендерим reveal, чтобы tip объяснил «сейчас появится знак…» ДО самой
  // карточки. После dismiss tip'а reveal появится автоматически (queue
  // не очищается).
  const currentTipId = useOnboardingStore((s) => s.current?.id ?? null);
  const head = queue[0] ?? null;

  // For minor toasts — track when shown so we can emit duration on dismiss.
  const shownIdRef = useRef<string | null>(null);
  const shownAtRef = useRef<number>(0);
  // Expanded state — when minor toast is tapped (not on close X), это раскрывает
  // его в полноценную showcase сцену с preview артефакта.
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (head === null) {
      shownIdRef.current = null;
      setExpanded(false);
      return;
    }
    const id =
      head.kind === 'single'
        ? head.artifact.id
        : `summary:${head.artifacts.map((a) => a.id).join(',')}`;

    if (shownIdRef.current !== id) {
      shownIdRef.current = id;
      shownAtRef.current = Date.now();
      setExpanded(false);
      if (head.kind === 'single') {
        emit('artifact.reveal_shown', {
          artifact_id: head.artifact.id,
          weight: head.artifact.weight,
        });
      }
    }
  }, [head]);

  if (head === null) return null;
  // Ждём пока «artifacts_explained» tip не закроется (только на самом
  // первом артефакте — последующие revealing'и идут сразу).
  if (currentTipId === 'artifacts_explained') return null;

  // Summary entries are always major.
  const isMajor = head.kind === 'summary' || head.artifact.weight === 'major';

  // Minor toast was tapped — render it as a major-style showcase.
  const renderAsMajor = isMajor || expanded;

  if (renderAsMajor) {
    return (
      <div className={styles.backdrop} role="dialog" aria-modal="true">
        <article className={styles.panel}>
          <p className={styles.preface}>память пути</p>
          {head.kind === 'single' ? (
            <>
              <h2 className={styles.title}>{head.artifact.displayName}</h2>
              {/* JB 2026-05-26: per-id preview вместо generic gold disc.
                  Игрок видит мини-визуал артефакта (rotating ring,
                  bright star, fractal, constellation и т.п.). */}
              <ArtifactPreview artifactId={head.artifact.id} size={130} />
              <p className={styles.revealLine}>{head.artifact.revealLine}</p>
              {/* JB 2026-05-26: «за что мы дали ту или иную память пути и
                  что её получение обозначает для геймплея». Под reveal-line:
                  «за {trigger}» — игрок понимает условие получения.
                  Description ниже говорит ЧТО физически появилось/изменилось. */}
              <p className={styles.triggerHint}>
                {describeArtifactTriggerPast(head.artifact.trigger)}
              </p>
              {head.artifact.description !== undefined && (
                <p className={styles.description}>{head.artifact.description}</p>
              )}
              <p className={styles.permanenceHint}>остаётся с тобой навсегда — твоя метка пути.</p>
            </>
          ) : (
            <>
              <h2 className={styles.title}>открылось ещё {head.artifacts.length.toString()}</h2>
              <ul className={styles.summaryList}>
                {head.artifacts.map((a) => (
                  <li key={a.id} className={styles.summaryItem}>
                    {a.displayName}
                  </li>
                ))}
              </ul>
            </>
          )}
          <DelayedButton onClick={dismiss} delayMs={1000} ariaLabel="Продолжить">
            хорошо
          </DelayedButton>
        </article>
      </div>
    );
  }

  // ---- Minor toast ----
  // At this point TS knows `head.kind === 'single'` — summary entries always
  // took the renderAsMajor branch above.
  return (
    <MinorToast
      title={head.artifact.displayName}
      body={head.artifact.revealLine}
      artifactId={head.artifact.id}
      triggerHint={describeArtifactTriggerPast(head.artifact.trigger)}
      onExpand={() => {
        setExpanded(true);
        emit('artifact.reveal_shown', {
          artifact_id: head.artifact.id,
          weight: 'major', // expanded view is a major-style reveal
        });
      }}
      onDismiss={() => {
        const durationMs = Date.now() - shownAtRef.current;
        emit('artifact.reveal_dismissed', {
          artifact_id: head.artifact.id,
          duration_ms: durationMs,
        });
        dismiss();
      }}
    />
  );
}

interface ToastProps {
  readonly title: string;
  readonly body: string;
  readonly artifactId: string;
  readonly triggerHint: string;
  readonly onExpand: () => void;
  readonly onDismiss: () => void;
}

function MinorToast({
  title,
  body,
  artifactId,
  triggerHint,
  onExpand,
  onDismiss,
}: ToastProps): JSX.Element {
  function handleDragEnd(_event: unknown, info: PanInfo): void {
    if (info.offset.x > SWIPE_DISMISS_PX) {
      onDismiss();
    }
  }

  return (
    <div className={styles.toastWrap}>
      <motion.div
        className={styles.toast}
        role="status"
        drag="x"
        dragConstraints={{ left: 0, right: 200 }}
        dragElastic={0.2}
        onDragEnd={handleDragEnd}
        initial={{ opacity: 0, x: 24 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 200 }}
        transition={{ duration: 0.35, ease: [0.25, 0.8, 0.3, 1] }}
      >
        <ArtifactPreview artifactId={artifactId} size={48} />
        <button
          type="button"
          className={styles.toastBody}
          onClick={onExpand}
          aria-label={`${title}: ${body} ${triggerHint} — показать подробнее`}
        >
          <p className={styles.toastTitle}>{title}</p>
          <p className={styles.toastText}>{body}</p>
          <p className={styles.toastTrigger}>{triggerHint}</p>
          <p className={styles.toastHint}>· нажми чтобы увидеть</p>
        </button>
        <button
          type="button"
          className={styles.toastClose}
          onClick={onDismiss}
          aria-label="Закрыть"
        >
          ✕
        </button>
      </motion.div>
    </div>
  );
}
